// AssetManagement.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Utility from "../utility/utility";
import { BASE_URL } from "../services/environment";

const AssetManagement = () => {
  const navigate = useNavigate();
  const [assetValue, setAssetValue] = useState(3);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchAssetConfig();
  }, []);

  const fetchAssetConfig = async () => {
    try {
      setLoading(true);
      setMessage("");

      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');

      if (!adminId || !apiKey) {
        navigate("/admin/auth");
        return;
      }

      const response = await fetch(`${BASE_URL}/api/admin/assets/config`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': adminId,
          'api-key': apiKey
        }
      });

      const data = await response.json();

      if (data.status_code === 200) {
        setAssetValue(data.data.assetValue);
      } else {
        Utility.showToast("error", data.message || "Failed to fetch asset configuration");
      }
    } catch (error) {
      Utility.showToast("error", "Failed to fetch asset configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");

      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');

      const response = await fetch(`${BASE_URL}/api/admin/assets/config`, {
        method: "PUT",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': adminId,
          'api-key': apiKey
        },
        body: JSON.stringify({
          assetValue: assetValue
        })
      });

      const data = await response.json();

      if (data.status_code === 200) {
        Utility.showToast("success", "Asset configuration updated successfully!");
        setMessage(`Asset value set to ${assetValue}. This will apply to all new game sessions.`);
      } else {
        Utility.showToast("error", data.message || "Failed to update asset configuration");
      }
    } catch (error) {
      Utility.showToast("error", "Failed to update asset configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleIncrement = () => {
    if (assetValue < 3) {
      setAssetValue(assetValue + 1);
    }
  };

  const handleDecrement = () => {
    if (assetValue > 1) {
      setAssetValue(assetValue - 1);
    }
  };

  const getAssetPreview = () => {
    return {
      "Time/Energy": assetValue,
      "Home/Family": assetValue,
      "Money/Debt": assetValue,
      "Work/Job": assetValue
    };
  };

  return (
    <div className="background">
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="text-white">Asset Management</h1>
          <button 
            onClick={() => navigate("/admin/dashboard")}
            className="btn btn-info semi-bold"
          >
            Back to Dashboard
          </button>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Configure Initial Assets</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-white" role="status">
                  <span className="visually-hidden"></span>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-muted">
                    Set the initial number of assets for each category when a new game starts.
                    This applies to all new game sessions.
                  </p>
                  <p className="text-muted">
                    <strong>Note:</strong> Assets can be 1-3.
                  </p>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Asset Value</h6>
                      </div>
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-center mb-4">
                          <button
                            onClick={handleDecrement}
                            className="btn btn-outline-primary btn-lg"
                            disabled={assetValue <= 1}
                          >
                            -
                          </button>
                          
                          <div className="mx-4 text-center">
                            <div className="display-1">{assetValue}</div>
                            <small className="text-muted">Current Value</small>
                          </div>
                          
                          <button
                            onClick={handleIncrement}
                            className="btn btn-outline-primary btn-lg"
                            disabled={assetValue >= 3}
                          >
                            +
                          </button>
                        </div>

                        <div className="form-range">
                          <input
                            type="range"
                            className="form-range w-100"
                            min="1"
                            max="3"
                            step="1"
                            value={assetValue}
                            onChange={(e) => setAssetValue(parseInt(e.target.value))}
                          />
                          <div className="d-flex justify-content-between">
                            <small>1 (Low)</small>
                            <small>2</small>
                            <small>3 (High)</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Asset Preview</h6>
                      </div>
                      <div className="card-body">
                        <p className="text-muted">This is what new players will start with:</p>
                        
                        <div className="list-group">
                          {Object.entries(getAssetPreview()).map(([key, value]) => (
                            <div key={key} className="list-group-item d-flex justify-content-between align-items-center">
                              <span className="font-weight-bold">{key}</span>
                              <span className="badge bg-primary rounded-pill">{value}</span>
                            </div>
                          ))}
                        </div>

                        {/* <div className="mt-3 p-2 bg-light rounded">
                          <small className="text-muted">
                            JSON Format:
                          </small>
                          <code className="d-block mt-1 p-2 bg-dark text-white rounded">
                            {JSON.stringify(getAssetPreview(), null, 2)}
                          </code>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>

                {message && (
                  <div className="alert alert-info">
                    {message}
                  </div>
                )}

                <div className="d-flex justify-content-between">
                  <button
                    onClick={fetchAssetConfig}
                    className="btn btn-info"
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Refresh"}
                  </button>
                  
                  <button
                    onClick={handleSave}
                    className="btn btn-info"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Configuration"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetManagement;